import { type BudgetBranch, type BudgetTree } from '../models/budgetTree';
import { areDimensionsEqual, type Dimension } from '../models/dimension';
import {
  type DimensionTree,
  type DimensionBranch,
} from '../models/dimensionTree';

export const getDimensionValueIds = (
  budgetTree: BudgetTree<unknown>,
  dimension: Dimension,
): string[] => {
  const traverseBranch = (branch: BudgetBranch<unknown>): string[] => {
    return branch.nodes.flatMap((node) => {
      const values =
        areDimensionsEqual(branch.dimension, dimension) &&
        node.values.type === 'specific'
          ? [node.values.value]
          : [];
      return values.concat(node.branch ? traverseBranch(node.branch) : []);
    });
  };

  return traverseBranch(budgetTree.branch);
};

export const transformDimensionTree = <
  InputDimension,
  OutputDimension,
  InputValues,
  OutputValues,
  InputOutcome,
  OutputOutcome,
>(
  tree: DimensionTree<InputDimension, InputValues, InputOutcome>,
  {
    transformDimension,
    transformValues,
    transformOutcome,
  }: {
    transformDimension: (dimension: InputDimension) => OutputDimension;
    transformValues: (values: InputValues) => OutputValues;
    transformOutcome: (outcome: InputOutcome) => OutputOutcome;
  },
): DimensionTree<OutputDimension, OutputValues, OutputOutcome> => {
  const transformBranch = (
    branch: DimensionBranch<InputDimension, InputValues, InputOutcome>,
  ): DimensionBranch<OutputDimension, OutputValues, OutputOutcome> => ({
    dimension: transformDimension(branch.dimension),
    nodes: branch.nodes.map((node) => ({
      values: transformValues(node.values),
      outcome: transformOutcome(node.outcome),
      branch: node.branch ? transformBranch(node.branch) : undefined,
    })),
  });
  return {
    outcome: transformOutcome(tree.outcome),
    branch: transformBranch(tree.branch),
  };
};
